import React, { useEffect, useMemo, useRef, useState } from "react";

import styles from "./suggestInput.module.scss";
import { foldForSearch } from "../../helper/searchFold";

/**
 * A text input that offers the values the field can actually hold.
 *
 * Genre and Profession are managed lists, so typing them free hand is
 * guessing: "Self Help" does not match the stored "Self-Help" and the list
 * comes back empty with nothing to say why. The suggestions are the stored
 * names, so picking one cannot miss.
 *
 * Still a text input, not a select. The endpoints match these fields by
 * regex, so a partial word is a legitimate search — the menu narrows what you
 * are typing rather than replacing it.
 */
const SuggestInput = ({
  value,
  onChange,
  suggestions,
  className,
  placeholder,
  ariaLabel,
  autoFocus,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  const matches = useMemo(() => {
    if (!suggestions?.length) return [];
    const query = foldForSearch((value || "").trim());
    const pool = query
      ? suggestions.filter((name) => foldForSearch(name).includes(query))
      : suggestions;
    // One exact match is not a menu — it is what you already typed.
    if (pool.length === 1 && foldForSearch(pool[0]) === query) return [];
    return pool.slice(0, 8);
  }, [suggestions, value]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setIsOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isOpen]);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <input
        type="text"
        className={className}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        autoComplete="off"
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
      />
      {isOpen && matches.length > 0 && (
        <div className={styles.menu} role="listbox">
          {matches.map((name) => (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={name === value}
              className={styles.item}
              // mousedown, not click: the input blurs first on a click and the
              // menu would be gone before the handler ever ran.
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(name);
                setIsOpen(false);
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestInput;
